import Image from "next/image";
import Link from "next/link";
import { canOptimizeExploreImage } from "../_lib/explore-image";
import type { ExploreStoryCard } from "../types";
import { STORY_BADGE_STYLES } from "./category-badge-styles";

interface MobileStoryHeroCardProps {
  story: ExploreStoryCard;
  exploreBase: string;
  priority?: boolean;
}

/**
 * New Yorker–style hero card for top / breaking stories on mobile.
 *
 * Layout: centred badge · large headline · summary paragraph ·
 * full-width hero image · metadata footer.
 * Gives the most important story a magazine-cover presence.
 */
export function MobileStoryHeroCard({
  story,
  exploreBase,
  priority = false,
}: MobileStoryHeroCardProps) {
  const badgeStyle = STORY_BADGE_STYLES[story.storyBadge];
  const primaryTag = story.productTags[0] ?? story.topicTags[0];
  const shouldOptimizeImage = canOptimizeExploreImage({
    src: story.coverImageUrl,
  });

  return (
    <Link
      className="group flex flex-col overflow-hidden rounded-2xl bg-[var(--explore-bg-card)] no-underline shadow-[var(--explore-card-shadow-lg)] active:scale-[0.995]"
      href={`${exploreBase}/stories/${story.storyId}`}
      style={{ fontFamily: "var(--homepage-sans-stack)" }}
    >
      {/* Editorial text block — centred, magazine feel */}
      <div className="flex flex-col items-center px-5 pt-5 pb-3 text-center">
        {/* Story badge */}
        <span
          className={`mb-3 rounded px-2.5 py-1 font-bold text-[0.65rem] uppercase tracking-[0.06em] ${badgeStyle}`}
        >
          {story.storyBadge}
        </span>

        {/* Headline — large, bold, generous wrapping */}
        <h3
          className="mb-2.5 line-clamp-5 text-balance font-bold text-[1.38rem] text-[var(--explore-text-primary)] leading-[1.2]"
          style={{ fontFamily: "var(--explore-card-title-stack)" }}
        >
          {story.headline}
        </h3>

        {/* Summary */}
        <p className="line-clamp-3 text-[0.88rem] text-[var(--explore-text-secondary)] leading-[1.55]">
          {story.summary}
        </p>
      </div>

      {/* Full-width hero image with inset */}
      <div className="relative mx-2 mb-2 aspect-[16/9] overflow-hidden rounded-xl bg-gradient-to-br from-[var(--explore-tag-bg)] to-[var(--explore-border-light)]">
        <Image
          alt={story.headline}
          className="h-full w-full object-cover"
          fill
          priority={priority}
          sizes="(max-width: 640px) 100vw, 50vw"
          src={story.coverImageUrl}
          unoptimized={!shouldOptimizeImage}
        />

        {story.isNew && (
          <div className="absolute top-2.5 left-2.5 z-10 flex items-center gap-1.5 rounded-full bg-[var(--explore-badge-overlay-bg)] px-2.5 py-1 shadow-[var(--explore-card-shadow)] backdrop-blur-[10px]">
            <span className="h-2 w-2 animate-[explore-pulse-dot_1.5s_ease-in-out_infinite] rounded-full bg-[var(--explore-new-indicator)] shadow-[0_0_0_3px_var(--explore-new-indicator-ring)]" />
            <span className="font-bold text-[0.65rem] text-[var(--explore-text-primary)] uppercase tracking-[0.03em]">
              New
            </span>
          </div>
        )}
      </div>

      {/* Metadata footer */}
      <div className="flex items-center justify-center gap-2 px-5 pt-1 pb-4 text-[0.72rem] text-[var(--explore-text-muted)]">
        {primaryTag && (
          <span className="rounded-full bg-[var(--explore-tag-bg)] px-2 py-0.5 font-medium text-[var(--explore-tag-text)]">
            {primaryTag.displayName}
          </span>
        )}
        <span>{story.timeAgo}</span>
        <span>·</span>
        <span>{story.readTime}</span>
      </div>
    </Link>
  );
}
